<template>
  <div class="roocode-task-message">
    <!-- 消息内容区 -->
    <div class="task-content">
      <!-- 🔥 新版：统一从 renderItems 渲染 -->
      <!-- 任务分隔符 -->
      <div
        v-for="(item, index) in renderItems"
        :key="`item-${index}-${item.type}-${item.taskId || ''}`"
        class="render-item"
      >
        <div v-if="item.type === 'task-divider'" class="task-divider">
          <div class="divider-line"></div>
          <div class="divider-label">
            <span class="divider-icon">{{ item.icon }}</span>
            <span>{{ item.label }}</span>
          </div>
          <div class="divider-line"></div>
        </div>

        <!-- Reasoning Section -->
        <div
          v-else-if="item.type === 'reasoning'"
          class="message-section reasoning-section"
          :class="{ 'is-collapsed': !item.streaming && !item.expanded }"
        >
          <div
            class="section-header clickable"
            @click="toggleReasoningExpand(index)"
          >
            <span class="section-icon-check" :class="{ 'is-complete': !item.streaming }"></span>
            <span class="section-title">{{ item.streaming ? '努力思考' : '思考完成' }}</span>
            <span v-if="!item.streaming" class="toggle-icon">
              {{ item.expanded ? '▼' : '▶' }}
            </span>
          </div>
          <div class="section-content reasoning-content">
            <unified-message-renderer
              :message="{ content: item.content, format: 'markdown' }"
              :streaming="item.streaming || false"
              :force-format="'markdown'"
            />
          </div>
        </div>

        <!-- Thinking Section -->
        <div v-else-if="item.type === 'thinking'" class="message-section thinking-section">
          <div class="section-header">
            <img :src="logoPath" class="section-logo" alt="傻蛋AI" />
            <span class="section-title">傻蛋AI</span>
          </div>
          <div class="section-content">
            <unified-message-renderer
              :message="{ content: item.content, format: 'markdown' }"
              :streaming="item.streaming || false"
              :force-format="'markdown'"
            />
          </div>
        </div>

        <!-- Tool Use Section -->
        <div v-else-if="item.type === 'tool'" class="message-section tool-section">
          <div class="section-header">
            <span class="section-icon-tool"></span>
            <span class="section-title">使用工具: {{ item.metadata.tool }}</span>
            <span v-if="item.metadata.status" class="tool-status" :class="`tool-${item.metadata.status}`">
              {{ item.metadata.status }}
            </span>
          </div>
          <div v-if="item.metadata.input" class="section-content tool-input">
            <pre>{{ formatToolInput(item.metadata.input) }}</pre>
          </div>
        </div>

        <!-- Completion Section -->
        <div v-else-if="item.type === 'completion'" class="message-section completion-section">
          <div class="section-header success-header">
            <span class="section-icon-success"></span>
            <span class="section-title">任务完成{{ isRootTaskCompletion(item) ? ' 🚀' : '' }}</span>
          </div>
          <div class="section-content completion-content">
            <unified-message-renderer
              :message="{ content: item.content, format: 'markdown' }"
              :streaming="item.streaming || false"
              :force-format="'markdown'"
            />
          </div>
        </div>

        <!-- Error Section -->
        <div v-else-if="item.type === 'error'" class="message-section error-section">
          <div class="section-header error-header">
            <span class="section-icon-error"></span>
            <span class="section-title">错误</span>
          </div>
          <div class="section-content error-content">
            <unified-message-renderer
              :message="{ content: item.content, format: 'markdown' }"
              :streaming="item.streaming || false"
              :force-format="'markdown'"
            />
          </div>
        </div>

        <!-- Command Output Section -->
        <div v-else-if="item.type === 'command_output'" class="message-section command-output-section">
          <div class="section-header">
            <span class="section-icon-terminal"></span>
            <span class="section-title">命令输出</span>

            <!-- 🔥 状态标签 -->
            <el-tag
              v-if="item.streaming"
              size="mini"
              type="warning"
              class="status-tag">
              <i class="el-icon-loading"></i> 执行中
            </el-tag>
            <el-tag
              v-else
              size="mini"
              :type="getExitCodeType(item)"
              class="status-tag">
              {{ getExitCodeIcon(item) }}
              Exit {{ item.exitCode || 0 }}
            </el-tag>

            <!-- 🔥 操作按钮 -->
            <div class="section-actions">
              <el-button
                size="mini"
                type="primary"
                icon="el-icon-view"
                @click="openFullTerminal(item)">
                📺 展开终端
              </el-button>
              <el-button
                v-if="item.streaming"
                size="mini"
                type="danger"
                icon="el-icon-close"
                @click="abortCommand(item)">
                中止
              </el-button>
            </div>
          </div>
          <div class="section-content command-output-content" :class="{ 'collapsed': isOutputCollapsed(item) }">
            <pre class="terminal-output">{{ item.content }}</pre>

            <!-- 🔥 展开/折叠按钮 -->
            <div v-if="shouldShowCollapseControl(item)" class="collapse-control">
              <el-button
                type="text"
                size="mini"
                @click="toggleOutputCollapse(item)">
                {{ isOutputCollapsed(item) ? '展开 ▼' : '折叠 ▲' }}
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- API Metrics (性能指标) -->
      <div v-if="showMetrics && apiMetrics" class="message-section metrics-section">
        <div class="section-header">
          <span class="section-icon">📊</span>
          <span class="section-title">性能指标</span>
        </div>
        <div class="section-content metrics-content">
          <div class="metric-item">
            <span class="metric-label">Token输入:</span>
            <span class="metric-value">{{ apiMetrics.tokensIn || 0 }}</span>
          </div>
          <div class="metric-item">
            <span class="metric-label">Token输出:</span>
            <span class="metric-value">{{ apiMetrics.tokensOut || 0 }}</span>
          </div>
          <div class="metric-item">
            <span class="metric-label">总成本:</span>
            <span class="metric-value">${{ (apiMetrics.cost || 0).toFixed(4) }}</span>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import UnifiedMessageRenderer from '../message/UnifiedMessageRenderer.vue';

export default {
  name: 'RooCodeTaskMessage',

  components: {
    UnifiedMessageRenderer
  },

  props: {
    streamId: {
      type: String,
      required: false
    },
    msgInfo: {
      type: Object,
      required: true
    },
    taskMode: {
      type: String,
      default: 'code'
    },
    showMetrics: {
      type: Boolean,
      default: false
    },
    // 🔥 新增：用于直接传递结构化数据块
    structuredChunks: {
      type: Array,
      default: () => []
    }
  },

  watch: {
    // 🔥 已废弃：chunk 处理现在由 AgentSessionManager 负责
    // renderItems 直接从 msgInfo._renderItems 获取（computed）
    // 'msgInfo._updateTrigger': {
    //   handler() {
    //     console.log('[RooCodeTaskMessage] _updateTrigger changed, chunks:', this.msgInfo._structuredChunks?.length);
    //     if (this.msgInfo._structuredChunks && this.msgInfo._structuredChunks.length > 0) {
    //       const startIndex = this.lastProcessedIndex || 0;
    //       const newItems = this.msgInfo._structuredChunks.slice(startIndex);

    //       if (newItems.length > 0) {
    //         newItems.forEach(chunk => {
    //           this.handleChunk(chunk);
    //         });
    //         this.lastProcessedIndex = this.msgInfo._structuredChunks.length;
    //       }
    //     }
    //   }
    // },

    // 🔥 监听整个消息的 streaming 状态，流式完成时发出事件
    'msgInfo.streaming': {
      handler(isStreaming) {
        if (!isStreaming && this.renderItems.length > 0) {
          // 🔥 流式完成，将所有 renderItems 的 streaming 设置为 false
          const finalizedItems = this.renderItems.map(item => ({
            ...item,
            streaming: false
          }));

          // 发出 finalizedItems 用于持久化
          this.$emit('task-completed', { renderItems: finalizedItems });
        }
      }
    },

    // 监听 msgInfo 变化，支持从持久化的 _finalContent 加载
    'msgInfo._finalContent': {
      handler(finalContent) {
        if (finalContent && this.msgInfo._isFinalized) {
          this.loadFinalizedContent(finalContent);
        }
      },
      immediate: true
    }
  },

  mounted() {
    console.log('[RooCodeTaskMessage] Component mounted, _renderItems:', this.msgInfo._renderItems?.length);
  },

  data() {
    return {
      // 各类型消息内容
      thinkingContent: '',
      reasoningContent: '',
      completionContent: '',
      errorContent: '',

      // 流式状态
      isThinkingStreaming: false,
      isReasoningStreaming: false,
      isCompletionStreaming: false,

      // 工具调用列表
      toolCalls: [],

      // 任务状态
      taskStatus: 'running', // running, completed, error

      apiMetrics: null,
      lastProcessedIndex: 0,

      // 🔥 任务分段相关
      currentTaskId: null,        // 当前任务ID
      currentMetadata: null,      // 当前任务元数据（包含 parentTaskId）
      taskSegments: [],           // 所有任务段列表
      // renderItems 已移至 computed，从 msgInfo._renderItems 获取

      // 🔥 命令输出折叠状态（使用 Map 存储每个 item 的状态）
      outputCollapseStates: new Map()
    };
  },

  computed: {
    // 根据环境动态获取 logo 路径
    logoPath() {
      // 检查是否在 void (VSCode) 环境中
      if (typeof window !== 'undefined' && window.vscodeServices) {
        return './im-components/logo-128.png';
      }
      // Web 环境使用 public 路径
      return '/logo-128.png';
    },

    statusText() {
      const statusMap = {
        running: '执行中',
        completed: '已完成',
        error: '执行失败'
      };
      return statusMap[this.taskStatus] || '';
    },

    isAnyStreaming() {
      return this.isThinkingStreaming || this.isReasoningStreaming || this.isCompletionStreaming;
    },

    thinkingMessage() {
      return { content: this.thinkingContent, format: 'markdown' };
    },

    reasoningMessage() {
      return { content: this.reasoningContent, format: 'markdown' };
    },

    completionMessage() {
      return { content: this.completionContent, format: 'markdown' };
    },

    errorMessage() {
      return { content: this.errorContent, format: 'markdown' };
    },

    // 🔥 renderItems 从 msgInfo._renderItems 获取（由 SessionManager 管理）
    renderItems() {
      const items = this.msgInfo._renderItems || [];
      console.log(`[RooCodeTaskMessage] renderItems computed, length: ${items.length}`, items.map(i => i.type));
      return items;
    }
  },

  methods: {
    /**
     * 🔥 判断是否为根任务完成
     */
    isRootTaskCompletion(item) {
      if (!item.taskId) return false;
      const parts = item.taskId.split('-');
      return parts.length <= 2;
    },

    /**
     * 🔥 加载已最终化的历史消息内容
     * 已废弃：现在 renderItems 直接从 msgInfo._renderItems 获取（由 SessionManager 管理）
     */
    loadFinalizedContent(finalContent) {
      // 🔥 不再需要加载 renderItems，因为它来自 computed 属性
      // renderItems 会自动从 msgInfo._renderItems 获取

      // 🔥 从 renderItems 提取状态信息
      const items = this.msgInfo._renderItems || [];
      if (items.length > 0) {
        const hasError = items.some(item => item.type === 'error');
        const hasCompletion = items.some(item => item.type === 'completion');

        if (hasCompletion) {
          this.taskStatus = 'completed';
        } else if (hasError) {
          this.taskStatus = 'error';
        }

        console.log('[RooCodeTaskMessage] 历史消息已加载:', items.length, 'items');
        return;
      }

      // 🔥 兼容旧版本（无 renderItems）
      if (finalContent.thinking) {
        this.thinkingContent = finalContent.thinking;
        this.isThinkingStreaming = false;
      }

      if (finalContent.reasoning) {
        this.reasoningContent = finalContent.reasoning;
        this.isReasoningStreaming = false;
      }

      if (finalContent.completion) {
        this.completionContent = finalContent.completion;
        this.isCompletionStreaming = false;
        this.taskStatus = 'completed';
      }

      if (finalContent.error) {
        this.errorContent = finalContent.error;
        this.taskStatus = 'error';
      }

      if (finalContent.toolCalls && finalContent.toolCalls.length > 0) {
        this.toolCalls = finalContent.toolCalls;
      }
    },

    /**
     * 处理流式数据块
     */
    handleChunk(chunk) {
      const { type, content, partial, ts, metadata } = chunk;

      // 检测任务切换（从 metadata 中获取 taskId）
      const taskId = metadata?.taskId;
      if (taskId && taskId !== this.currentTaskId) {
        this.handleTaskSwitch(taskId, metadata);
      }

      switch (type) {
        case 'thinking':
        case 'text':
          this.handleThinking(content, partial, ts);
          break;

        case 'reasoning':
          this.handleReasoning(content, partial, ts);
          break;

        case 'tool_use':
        case 'tool':
          this.handleToolUse(metadata, partial, ts);
          break;

        case 'command_output':
          this.handleCommandOutput(content, partial, ts);
          break;

        case 'completion':
        case 'completion_result':
          this.handleCompletion(content, partial, ts);
          break;

        case 'error':
          this.handleError(content, partial, ts);
          break;

        case 'api_req_started':
        case 'api_req_finished':
          this.handleApiMetrics(content);
          break;

        default:
          // 忽略未知类型
          break;
      }

      // 🔥 检查所有 section 是否都已结束流式
      this.checkAndUpdateStreamingStatus();

      // 触发滚动
      this.$emit('need-scroll');
    },

    /**
     * 🔥 检查所有 section 的 streaming 状态，如果都结束了就更新 msgInfo.streaming
     */
    checkAndUpdateStreamingStatus() {
      // 检查是否有任何 section 还在流式中
      const hasStreamingSection = this.renderItems.some(item => item.streaming);

      if (!hasStreamingSection && this.msgInfo.streaming) {
        // 所有 section 都结束了，更新 msgInfo.streaming
        this.$set(this.msgInfo, 'streaming', false);
      }
    },

    /**
     * 🔥 处理任务切换（插入分隔符到 renderItems）
     */
    handleTaskSwitch(newTaskId, metadata) {
      if (this.currentTaskId === null) {
        // 第一个任务，不添加分隔符
        this.currentTaskId = newTaskId;
        this.currentMetadata = metadata;
        return;
      }

      // 判断是子任务还是返回主任务
      const isSubtask = this.isSubtask(newTaskId, metadata);
      const isReturning = this.isReturning(newTaskId, metadata);

      // 🔥 在 renderItems 中插入分隔符
      let dividerIcon, dividerLabel;

      if (isReturning) {
        // 返回父任务：↩️ + "任务完成总结"
        dividerIcon = '↩️';
        dividerLabel = '任务完成总结';
      } else {
        // 进入新任务（包括子任务和并行任务）
        const taskParts = newTaskId.split('-');
        const isRootTask = taskParts.length <= 2;

        if (isRootTask) {
          // 主任务
          dividerIcon = '🔀';
          dividerLabel = '主任务';
        } else {
          // 子任务
          dividerIcon = '🔀';
          dividerLabel = this.getTaskLabel(newTaskId);
        }
      }

      this.renderItems.push({
        type: 'task-divider',
        icon: dividerIcon,
        label: dividerLabel,
        taskId: newTaskId
      });

      console.log(`[RooCodeTaskMessage] 任务切换: ${this.currentTaskId} -> ${newTaskId}, isSubtask: ${isSubtask}, isReturning: ${isReturning}, parentTaskId: ${metadata?.parentTaskId}`);

      this.currentTaskId = newTaskId;
      this.currentMetadata = metadata;
    },

    /**
     * 🔥 判断是否为子任务（使用 parentTaskId 判断）
     */
    isSubtask(taskId, metadata) {
      // 🔥 关键修复：使用 metadata.parentTaskId 判断是否为子任务
      // 如果新任务的 parentTaskId 等于当前任务ID，说明是进入子任务
      return metadata?.parentTaskId === this.currentTaskId;
    },

    /**
     * 🔥 判断是否返回父任务（使用 parentTaskId 判断）
     */
    isReturning(taskId, metadata) {
      // 🔥 关键修复：使用 metadata.parentTaskId 判断是否返回父任务
      // 如果新任务ID等于当前任务的 parentTaskId，说明是返回父任务
      return taskId === this.currentMetadata?.parentTaskId;
    },

    /**
     * 🔥 获取任务标签
     */
    getTaskLabel(taskId) {
      const parts = taskId.split('-');
      if (parts.length <= 2) return '主任务';
      const subId = parts[parts.length - 1];
      return `子任务 (${subId.substring(0, 6)})`;
    },

    /**
     * 🔥 折叠上一个 reasoning 块（如果存在且未折叠）
     */
    collapseLastReasoning() {
      const lastReasoning = this.findLastItemOfType('reasoning', this.currentTaskId);
      if (lastReasoning && lastReasoning.streaming) {
        lastReasoning.streaming = false;
        this.$set(lastReasoning, 'expanded', false);

        // 🔥 更新 isReasoningStreaming 状态
        this.isReasoningStreaming = this.renderItems.some(
          item => item.type === 'reasoning' && item.streaming
        );
      }
    },

    /**
     * 🔥 处理 thinking - 向 renderItems 添加/更新 section
     */
    handleThinking(content, partial, ts) {
      if (!content) return;

      // 🔥 新的 thinking 块开始时，折叠上一个 reasoning 块
      this.collapseLastReasoning();

      // 查找当前任务的最后一个 thinking section
      const lastThinking = this.findLastItemOfType('thinking', this.currentTaskId);

      // 🔥 修复：使用 ts 时间戳区分不同的 thinking 块
      // 只有当 streaming=true 且 ts 相同时才追加，否则创建新的
      if (lastThinking && lastThinking.streaming && lastThinking.ts === ts) {
        // 追加到现有的流式 section
        lastThinking.content += content;
        lastThinking.streaming = partial; // partial=false 时，标记流式结束
      } else {
        // 🔥 创建新块时，先结束上一个块
        if (lastThinking && lastThinking.streaming) {
          lastThinking.streaming = false;
          console.log(`[handleThinking] 💡 前一个 thinking 块被新块中断，强制结束`);
        }

        // 创建新的 thinking section（ts 不同或者前一个已结束）
        this.renderItems.push({
          type: 'thinking',
          content: content,
          streaming: partial,
          taskId: this.currentTaskId,
          ts: ts
        });
      }

      // 保持向后兼容（用于旧逻辑）
      this.thinkingContent += content;
      this.isThinkingStreaming = partial;
    },

    /**
     * 🔥 处理 reasoning - 向 renderItems 添加/更新 section
     */
    handleReasoning(content, partial, ts) {
      if (!content) return;

      const lastReasoning = this.findLastItemOfType('reasoning', this.currentTaskId);

      // 🔥 核心逻辑：ts 变化 = 新块开始 = 上一块结束
      if (lastReasoning && lastReasoning.ts === ts) {
        // 相同 ts，追加内容
        lastReasoning.content += content;
        // streaming 保持 true，因为同一个 ts 的内容还在流式输出
      } else {
        // 不同 ts，说明新块开始，上一个块结束
        if (lastReasoning) {
          lastReasoning.streaming = false;
          this.$set(lastReasoning, 'expanded', false);
        }
        // 创建新的 reasoning block
        this.renderItems.push({
          type: 'reasoning',
          content: content,
          streaming: true,  // 🔥 新块开始，默认流式中
          taskId: this.currentTaskId,
          ts: ts,
          expanded: true  // 🔥 新块默认展开
        });
      }

      this.reasoningContent += content;
      // 🔥 检查是否有正在流式的 reasoning 块
      this.isReasoningStreaming = this.renderItems.some(
        item => item.type === 'reasoning' && item.streaming
      );
    },

    /**
     * 🔥 处理工具调用 - 向 renderItems 添加 section
     */
    handleToolUse(metadata, partial, ts) {
      if (!metadata) return;

      // 🔥 新的 tool 块开始时，折叠上一个 reasoning 块
      this.collapseLastReasoning();

      // 查找现有的 tool section
      const existingTool = this.renderItems.find(item =>
        item.type === 'tool' && item.ts === ts
      );

      if (existingTool) {
        // 更新现有工具调用
        existingTool.metadata = { ...existingTool.metadata, ...metadata };
        existingTool.streaming = partial;
      } else {
        // 创建新的 tool section
        this.renderItems.push({
          type: 'tool',
          metadata: {
            tool: metadata.tool,
            status: metadata.status,
            input: metadata.input
          },
          streaming: partial,
          taskId: this.currentTaskId,
          ts: ts
        });
      }

      // 向后兼容
      let toolCall = this.toolCalls.find(t => t.ts === ts);
      if (!toolCall) {
        this.toolCalls.push({ ts, tool: metadata.tool, status: metadata.status, input: metadata.input });
      } else {
        toolCall.status = metadata.status || toolCall.status;
        toolCall.input = metadata.input || toolCall.input;
      }
    },

    /**
     * 🔥 处理 completion - 向 renderItems 添加/更新 section
     */
    handleCompletion(content, partial, ts) {
      if (!content) return;

      // 🔥 新的 completion 块开始时，折叠上一个 reasoning 块
      this.collapseLastReasoning();

      const lastCompletion = this.findLastItemOfType('completion', this.currentTaskId);

      // 🔥 修复：使用 ts 时间戳区分不同的 completion 块
      // 只有当 streaming=true 且 ts 相同时才追加，否则创建新的
      if (lastCompletion && lastCompletion.streaming && lastCompletion.ts === ts) {
        lastCompletion.content += content;
        lastCompletion.streaming = partial;
      } else {
        // 创建新的 completion block（ts 不同或者前一个已结束）
        this.renderItems.push({
          type: 'completion',
          content: content,
          streaming: partial,
          taskId: this.currentTaskId,
          ts: ts
        });
      }

      this.completionContent += content;
      this.isCompletionStreaming = partial;

      if (!partial) {
        this.taskStatus = 'completed';
        this.$emit('task-completed', { content: this.completionContent });
      }
    },

    /**
     * 🔥 处理命令输出 - 向 renderItems 添加/更新 section
     */
    handleCommandOutput(content, partial, ts) {
      if (!content) return;

      // 🔥 优先使用 prop 传入的 streamId，这是正确的 WebSocket streamId
      const streamId = this.streamId || this.msgInfo.streamId || this.msgInfo.tmpId;

      // 查找当前任务的 command_output section
      const existingOutput = this.findLastItemOfType('command_output', this.currentTaskId);

      if (existingOutput && existingOutput.streaming) {
        // 累积输出（流式追加）
        existingOutput.content += content;
        existingOutput.streaming = partial;
        existingOutput.ts = ts;
      } else {
        // 创建新的 command_output section
        this.renderItems.push({
          type: 'command_output',
          content: content,
          streaming: partial,
          taskId: this.currentTaskId,
          ts: ts,
          streamId: streamId
        });
      }
    },

    /**
     * 🔥 处理错误 - 向 renderItems 添加/更新 section
     */
    handleError(content, partial, ts) {
      if (!content) return;

      // 🔥 新的 error 块开始时，折叠上一个 reasoning 块
      this.collapseLastReasoning();

      const lastError = this.findLastItemOfType('error', this.currentTaskId);

      if (lastError && lastError.streaming) {
        lastError.content += content;
        lastError.streaming = partial;
      } else {
        this.renderItems.push({
          type: 'error',
          content: content,
          streaming: partial,
          taskId: this.currentTaskId,
          ts: ts
        });
      }

      this.errorContent += content;
      this.taskStatus = 'error';

      if (!partial) {
        this.$emit('task-error', { error: this.errorContent });
      }
    },

    /**
     * 🔥 辅助方法：查找当前任务的最后一个指定类型的 item
     */
    findLastItemOfType(type, taskId) {
      for (let i = this.renderItems.length - 1; i >= 0; i--) {
        const item = this.renderItems[i];
        if (item.type === type && item.taskId === taskId) {
          return item;
        }
      }
      return null;
    },

    /**
     * 🔥 修复历史消息中任务分隔符的标签
     * 分析任务切换模式，将返回父任务的分隔符改为"任务完成总结"
     */
    fixTaskDividerLabels() {
      for (let i = 0; i < this.renderItems.length; i++) {
        const item = this.renderItems[i];

        if (item.type !== 'task-divider') continue;

        // 🔥 判断是否为返回父任务的分隔符
        // 方法：查找分隔符前后的任务ID，判断是从子任务返回父任务
        const prevTaskId = this.findPreviousTaskId(i);
        const currentTaskId = item.taskId;

        if (prevTaskId && currentTaskId) {
          const isReturningToParent = this.isTaskIdReturning(prevTaskId, currentTaskId);

          if (isReturningToParent) {
            // 从子任务返回父任务，修改标签
            item.label = '任务完成总结';
            item.icon = '↩️';
          }
        }
      }
    },

    /**
     * 🔥 辅助方法：查找分隔符之前的任务ID
     */
    findPreviousTaskId(dividerIndex) {
      for (let i = dividerIndex - 1; i >= 0; i--) {
        const item = this.renderItems[i];
        if (item.taskId && item.type !== 'task-divider') {
          return item.taskId;
        }
      }
      return null;
    },

    /**
     * 🔥 辅助方法：判断是否为返回父任务（通过 taskId 结构判断）
     */
    isTaskIdReturning(fromTaskId, toTaskId) {
      // 从子任务（格式：uuid-uuid-xxx）返回父任务（格式：uuid-uuid）
      const fromParts = fromTaskId.split('-');
      const toParts = toTaskId.split('-');

      // 如果 from 是子任务（3段或更多），to 是父任务（2段）
      // 且 to 是 from 的前缀，说明是返回父任务
      if (fromParts.length > 2 && toParts.length <= 2) {
        const fromPrefix = fromParts.slice(0, 2).join('-');
        const toPrefix = toParts.join('-');
        return fromPrefix === toPrefix;
      }

      return false;
    },

    handleApiMetrics(content) {
      if (!content) return;

      try {
        const metricsData = typeof content === 'string' ? JSON.parse(content) : content;
        this.apiMetrics = {
          tokensIn: metricsData.tokensIn || 0,
          tokensOut: metricsData.tokensOut || 0,
          cost: metricsData.cost || 0
        };
      } catch (error) {
        console.error('[RooCodeTaskMessage] 解析 API 指标失败:', error);
      }
    },

    /**
     * 格式化工具输入
     */
    formatToolInput(input) {
      if (typeof input === 'string') {
        try {
          return JSON.stringify(JSON.parse(input), null, 2);
        } catch {
          return input;
        }
      }
      return JSON.stringify(input, null, 2);
    },

    /**
     * 🔥 切换 reasoning 块的展开/折叠状态
     */
    toggleReasoningExpand(index) {
      const item = this.renderItems[index];
      if (item && item.type === 'reasoning' && !item.streaming) {
        // 使用 Vue.set 来确保响应式
        this.$set(item, 'expanded', !item.expanded);
      }
    },

    /**
     * 🔥 打开完整终端弹窗
     */
    openFullTerminal(item) {
      // 🔥 强制读取 content 值（Vue 响应式对象需要强制求值）
      let actualContent = '';

      if (item.content) {
        // 如果是 getter 函数，调用它
        if (typeof item.content === 'function') {
          actualContent = item.content();
        } else {
          // 强制转换为字符串（处理响应式对象）
          actualContent = '' + item.content;
        }
      }

      this.$emit('open-terminal', {
        streamId: item.streamId || this.streamId,  // 🔥 优先使用 section 的 streamId
        taskId: item.taskId,
        content: actualContent,
        cwd: this.msgInfo.cwd || '~',
        ts: item.ts
      });
    },

    /**
     * 🔥 中止命令执行
     */
    abortCommand(item) {
      console.log('[RooCodeTaskMessage] 中止命令:', item);
      this.$emit('abort-command', {
        streamId: this.streamId,
        taskId: item.taskId,
        ts: item.ts
      });
    },

    /**
     * 🔥 获取退出码类型（用于标签颜色）
     */
    getExitCodeType(item) {
      const exitCode = item.exitCode || 0;
      return exitCode === 0 ? 'success' : 'danger';
    },

    /**
     * 🔥 获取退出码图标
     */
    getExitCodeIcon(item) {
      const exitCode = item.exitCode || 0;
      return exitCode === 0 ? '✓' : '✗';
    },

    /**
     * 🔥 判断是否应该显示折叠控制
     */
    shouldShowCollapseControl(item) {
      // 内容超过 1000 字符才显示折叠控制
      return item.content && item.content.length > 1000;
    },

    /**
     * 🔥 判断输出是否折叠
     */
    isOutputCollapsed(item) {
      const key = this.getItemKey(item);
      // 默认折叠长内容
      if (!this.outputCollapseStates.has(key) && this.shouldShowCollapseControl(item)) {
        return true;
      }
      return this.outputCollapseStates.get(key) !== false;
    },

    /**
     * 🔥 切换输出折叠状态
     */
    toggleOutputCollapse(item) {
      const key = this.getItemKey(item);
      const currentState = this.isOutputCollapsed(item);
      this.outputCollapseStates.set(key, !currentState);
      // 强制更新视图
      this.$forceUpdate();
    },

    /**
     * 🔥 获取 item 的唯一键
     */
    getItemKey(item) {
      return `${item.taskId || 'default'}_${item.ts || Date.now()}`;
    },

    clear() {
      this.thinkingContent = '';
      this.reasoningContent = '';
      this.completionContent = '';
      this.errorContent = '';
      this.isThinkingStreaming = false;
      this.isReasoningStreaming = false;
      this.isCompletionStreaming = false;
      this.toolCalls = [];
      this.taskStatus = 'running';
      this.apiMetrics = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.roocode-task-message {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .task-header {
    display: flex;
    align-items: center;
    gap: 12px;

    .task-avatar {
      width: 38px;
      height: 38px;
      border-radius: 50%;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      display: flex;
      align-items: center;
      justify-content: center;

      .task-logo {
        font-size: 24px;
        line-height: 1;
      }
    }

    .task-info {
      flex: 1;

      .task-title {
        display: flex;
        align-items: center;
        gap: 8px;

        .task-name {
          font-weight: 600;
          font-size: 14px;
          color: var(--im-text-color);
        }

        .task-mode {
          font-size: 12px;
          color: var(--im-text-color-light);
          background: var(--im-background);
          padding: 2px 8px;
          border-radius: 4px;
        }
      }

      .task-status {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 4px;
        font-size: 12px;

        .status-icon {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }

        &.status-running {
          color: #1890ff;
          .status-icon {
            background: #1890ff;
            animation: pulse 1.5s ease-in-out infinite;
          }
        }

        &.status-completed {
          color: #52c41a;
          .status-icon {
            background: #52c41a;
          }
        }

        &.status-error {
          color: #ff4d4f;
          .status-icon {
            background: #ff4d4f;
          }
        }
      }
    }
  }

  .task-content {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .message-section {
      border-radius: 8px;
      overflow: hidden;

      .section-header {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        background: rgba(0, 0, 0, 0.02);
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);

        &.clickable {
          cursor: pointer;
          user-select: none;
          transition: background 0.2s;

          &:hover {
            background: rgba(0, 0, 0, 0.04);
          }
        }

        .section-icon {
          font-size: 16px;
          margin-right: 4px;
        }

        // 🔥 状态标签样式
        .status-tag {
          margin-left: 8px;
          font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
        }

        // 🔥 操作按钮容器
        .section-actions {
          display: flex;
          gap: 8px;
          margin-left: auto;
        }

        // 🔥 精致的线条图标 - 勾选
        .section-icon-check {
          width: 16px;
          height: 16px;
          margin-right: 8px;
          border: 1.5px solid rgba(24, 144, 255, 0.5);
          border-radius: 50%;
          position: relative;
          display: inline-block;
          vertical-align: middle;
          transition: all 0.3s ease;

          // 勾选标记
          &::after {
            content: '';
            position: absolute;
            left: 50%;
            top: 50%;
            width: 4px;
            height: 7px;
            border: solid rgba(82, 196, 26, 0.8);
            border-width: 0 1.5px 1.5px 0;
            transform: translate(-50%, -60%) rotate(45deg);
            opacity: 0;
            transition: opacity 0.3s ease;
          }

          // 完成状态
          &.is-complete {
            border-color: rgba(82, 196, 26, 0.6);

            &::after {
              opacity: 1;
            }
          }

          // 进行中脉冲
          &:not(.is-complete) {
            animation: pulse-border 2s ease-in-out infinite;
          }
        }

        @keyframes pulse-border {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
        }

        // 🔥 精致的线条图标 - 工具
        .section-icon-tool {
          width: 16px;
          height: 16px;
          margin-right: 8px;
          position: relative;
          display: inline-block;
          vertical-align: middle;

          // 扳手主体
          &::before {
            content: '';
            position: absolute;
            left: 3px;
            top: 2px;
            width: 10px;
            height: 10px;
            border: 1.5px solid rgba(250, 173, 20, 0.6);
            border-radius: 2px;
            transform: rotate(-45deg);
          }

          // 扳手手柄
          &::after {
            content: '';
            position: absolute;
            right: 1px;
            bottom: 1px;
            width: 5px;
            height: 1.5px;
            background: rgba(250, 173, 20, 0.6);
            transform: rotate(-45deg);
          }
        }

        // 🔥 精致的线条图标 - 成功
        .section-icon-success {
          width: 16px;
          height: 16px;
          margin-right: 8px;
          border: 1.5px solid rgba(82, 196, 26, 0.6);
          border-radius: 50%;
          position: relative;
          display: inline-block;
          vertical-align: middle;

          // 勾选标记
          &::after {
            content: '';
            position: absolute;
            left: 50%;
            top: 50%;
            width: 4px;
            height: 7px;
            border: solid rgba(82, 196, 26, 0.8);
            border-width: 0 1.5px 1.5px 0;
            transform: translate(-50%, -60%) rotate(45deg);
          }
        }

        // 🔥 精致的线条图标 - 错误
        .section-icon-error {
          width: 16px;
          height: 16px;
          margin-right: 8px;
          border: 1.5px solid rgba(255, 77, 79, 0.6);
          border-radius: 50%;
          position: relative;
          display: inline-block;
          vertical-align: middle;

          // X 标记 - 左上到右下
          &::before {
            content: '';
            position: absolute;
            left: 50%;
            top: 50%;
            width: 8px;
            height: 1.5px;
            background: rgba(255, 77, 79, 0.8);
            transform: translate(-50%, -50%) rotate(45deg);
          }

          // X 标记 - 右上到左下
          &::after {
            content: '';
            position: absolute;
            left: 50%;
            top: 50%;
            width: 8px;
            height: 1.5px;
            background: rgba(255, 77, 79, 0.8);
            transform: translate(-50%, -50%) rotate(-45deg);
          }
        }

        // 🔥 精致的线条图标 - 终端
        .section-icon-terminal {
          width: 16px;
          height: 16px;
          margin-right: 8px;
          border: 1.5px solid rgba(24, 144, 255, 0.6);
          border-radius: 3px;
          position: relative;
          display: inline-block;
          vertical-align: middle;

          // > 提示符
          &::before {
            content: '';
            position: absolute;
            left: 3px;
            top: 50%;
            width: 0;
            height: 0;
            border-left: 4px solid rgba(24, 144, 255, 0.8);
            border-top: 3px solid transparent;
            border-bottom: 3px solid transparent;
            transform: translateY(-50%);
          }

          // 下划线
          &::after {
            content: '';
            position: absolute;
            right: 3px;
            bottom: 4px;
            width: 4px;
            height: 1.5px;
            background: rgba(24, 144, 255, 0.8);
          }
        }

        .section-logo {
          width: 20px;
          height: 20px;
          margin-right: 6px;
          vertical-align: middle;
        }

        .section-title {
          flex: 1;
          font-size: 13px;
          font-weight: 500;
          color: var(--im-text-color);
        }

        .toggle-icon {
          font-size: 12px;
          color: var(--im-text-color-light, #909399);
          transition: transform 0.2s;
        }

        .tool-status {
          font-size: 11px;
          padding: 2px 6px;
          border-radius: 3px;

          &.tool-pending {
            background: #faad14;
            color: white;
          }

          &.tool-success {
            background: #52c41a;
            color: white;
          }

          &.tool-error {
            background: #ff4d4f;
            color: white;
          }
        }

        &.success-header {
          background: rgba(82, 196, 26, 0.1);
          border-bottom-color: rgba(82, 196, 26, 0.2);

          .section-title {
            color: #52c41a;
            font-weight: 600;
          }
        }

        &.error-header {
          background: rgba(255, 77, 79, 0.1);
          border-bottom-color: rgba(255, 77, 79, 0.2);

          .section-title {
            color: #ff4d4f;
            font-weight: 600;
          }
        }
      }

      .section-content {
        padding: 12px;
        background: white;
        transition: max-height 0.3s ease-out, padding 0.3s ease-out;
        overflow: hidden;

        &.thinking-content,
        &.reasoning-content,
        &.completion-content {
          line-height: 1.6;
          color: var(--im-text-color);
        }

        &.tool-input {
          pre {
            margin: 0;
            padding: 8px;
            background: rgba(0, 0, 0, 0.03);
            border-radius: 4px;
            font-size: 12px;
            overflow-x: auto;
          }
        }

        &.error-content {
          color: #ff4d4f;

          p {
            margin: 0;
            word-break: break-word;
          }
        }

        &.command-output-content {
          padding: 0;
          background: rgba(0, 0, 0, 0.03);
          position: relative;

          .terminal-output {
            margin: 0;
            padding: 12px;
            background: #1e1e1e;
            color: #d4d4d4;
            font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.5;
            overflow-x: auto;
            white-space: pre;
            border-radius: 4px;
          }

          // 🔥 折叠状态
          &.collapsed {
            max-height: 200px;
            overflow: hidden;

            &::after {
              content: '';
              position: absolute;
              bottom: 0;
              left: 0;
              right: 0;
              height: 50px;
              background: linear-gradient(transparent, #1e1e1e);
              pointer-events: none;
            }
          }

          // 🔥 折叠控制按钮
          .collapse-control {
            text-align: center;
            padding: 8px;
            background: #2d2d2d;
            border-top: 1px solid #404040;

            .el-button--text {
              color: #d4d4d4;

              &:hover {
                color: #61afef;
              }
            }
          }
        }

        &.metrics-content {
          display: flex;
          gap: 16px;

          .metric-item {
            display: flex;
            flex-direction: column;
            gap: 4px;

            .metric-label {
              font-size: 11px;
              color: var(--im-text-color-light);
            }

            .metric-value {
              font-size: 14px;
              font-weight: 600;
              color: var(--im-text-color);
            }
          }
        }
      }

      // 🔥 折叠状态样式 - 手风琴效果，完全隐藏内容
      &.is-collapsed {
        .section-content {
          display: none; // 完全隐藏内容区域
        }
      }
    }
  }

  .streaming-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: rgba(24, 144, 255, 0.1);
    border-radius: 6px;

    .streaming-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #1890ff;
      animation: pulse 1.5s ease-in-out infinite;
    }

    .streaming-text {
      font-size: 12px;
      color: #1890ff;
    }
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.2);
  }
}

// 确保 Markdown 内容样式正确
::v-deep .unified-message-renderer {
  .markdown-body {
    background: transparent !important;
    padding: 0 !important;

    p {
      margin: 0 0 8px 0;

      &:last-child {
        margin-bottom: 0;
      }
    }

    h1, h2, h3, h4, h5, h6 {
      margin: 12px 0 8px 0;

      &:first-child {
        margin-top: 0;
      }
    }

    ul, ol {
      margin: 8px 0;
      padding-left: 24px;
    }

    code {
      background: rgba(0, 0, 0, 0.05);
      padding: 2px 6px;
      border-radius: 3px;
      font-size: 0.9em;
    }

    pre {
      background: rgba(0, 0, 0, 0.03);
      padding: 12px;
      border-radius: 6px;
      overflow-x: auto;

      code {
        background: none;
        padding: 0;
      }
    }
  }

  // 🔥 渲染项容器（透明，不影响布局）
  .render-item {
    // 不添加任何样式，保持透明
  }

  // 🔥 任务分隔符样式
  .task-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 16px 0;
    opacity: 0.7;

    .divider-line {
      flex: 1;
      height: 1px;
      background: linear-gradient(
        to right,
        transparent,
        var(--im-border-color),
        transparent
      );
    }

    .divider-label {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      background: var(--im-background);
      border: 1px solid var(--im-border-color);
      border-radius: 12px;
      font-size: 12px;
      color: var(--im-text-color-light);
      white-space: nowrap;

      .divider-icon {
        font-size: 14px;
      }
    }
  }
}
</style>
